import { FileExcelOutlined, FolderOpenOutlined, PrinterOutlined, RedoOutlined, SaveOutlined } from "@ant-design/icons";
import { Alert, Button, Col, DatePickerProps, Flex, Form, Image, Modal, Row, Select, Switch, Table, Tooltip } from "antd";
import DatePicker from "antd/es/date-picker";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import ModalComposicoesSalvas from "../../components/composicoesSalvas/modalComposicoesSalvas";
import TableComponent from "../../components/table/tableComponent";
import { ComposicaoSalvaModel } from "../../models/composicao/composicaoSalvaModel";
import { FechamentoResultadoSorteioModel } from "../../models/composicao/fechamentoResultadoSorteioModel";
import { ListarComposicaoModel } from "../../models/composicao/listarComposicaoModel";
import { SalvarComposicaoModel } from "../../models/composicao/salvarComposicaoModel";
import { EmpresaModel } from "../../models/empresa/empresaModel";
import { setBuscaComposicao, setExibindoComposicaoSalva } from "../../redux/slices/composicao/composicaoSlice";
import { setTitle } from "../../redux/slices/configuracoes/configuracoesSlice";
import { listarComposicao, obterComposicaoSalva, salvarComposicao, verificarComposicaoSalva } from "../../services/composicaoService";
import { buscarEmpresa } from "../../services/empresaService";
import icon from "../../util/assets/icon_login.png";
import { formatDataHoraLocal } from "../../util/dataHoraAuxiliar";
import { currencyBRL, formatPercent } from "../../util/dinheiroBRAuxiliar";
import { useAppDispatch, useAppSelector } from "../../util/hooks";
import "./style/index.scss";

dayjs.locale("pt-br");

const tipoDataOptions = [
  { label: "Sorteio", value: "SORT" },
  { label: "Lançamento", value: "LANC" },
];

const SONHO_IDS = [64, 65];

// % sobre a Receita Operacional. Despesas/Premiação usam sinal invertido (valor negativo -> % positivo)
const percentualComposicao = (valor: number | null, base: number | null, inverterSinal = false): number | null => {
  if (valor === null || valor === undefined || !base) return null;
  const p = valor / base;
  return inverterSinal ? -p : p;
};

const percentualTexto = (valor: number | null, base: number | null, inverterSinal = false): string => {
  const p = percentualComposicao(valor, base, inverterSinal);
  return p === null ? "" : formatPercent(p);
};

const percentualCelula = (valor: number | null, base: number | null, inverterSinal = false) => {
  const p = percentualComposicao(valor, base, inverterSinal);
  return p !== null && <p style={{ color: p < 0 ? "red" : "blue", marginBottom: "0px" }}>{formatPercent(p)}</p>;
};

interface RelatorioHtmlProps {
  busca: ListarComposicaoModel;
  list: FechamentoResultadoSorteioModel[];
  listSonho: FechamentoResultadoSorteioModel[];
  empresaList: EmpresaModel[];
}

const RelatorioHtmlTable = ({ data, titulo, somenteTotal = false }: { data: FechamentoResultadoSorteioModel[]; titulo?: string; somenteTotal?: boolean }) => {
  const total = (seletor: (item: FechamentoResultadoSorteioModel) => number | null) => data.reduce((acc, item) => acc + (seletor(item) || 0), 0);
  const totalReceitaOperacional = total((i) => i.valorReceitaOperacional);
  const totalDespesas = total((i) => i.valorDespesas);
  const totalPremiacao = total((i) => i.valorPremiacao);
  const totalLucroBruto = total((i) => i.valorLucroBruto);
  const totalLucroLiquido = total((i) => i.valorLucroLiquido);

  return (
    <div className="rel-detalhes-composicao">
      {titulo && <h3 style={{ marginTop: "16px", marginBottom: "4px", marginLeft: "14px" }}>{titulo}</h3>}
      <table className="tb-dados">
        <thead>
          <tr className="header-table">
            <th className="ht-left">Empresa</th>
            <th className="ht-right">Receitas Operacionais</th>
            <th className="ht-right">Outras Receitas</th>
            <th className="ht-right">Receita Total</th>
            <th className="ht-right">Despesas</th>
            <th className="ht-right">%</th>
            <th className="ht-right">Premiação</th>
            <th className="ht-right">%</th>
            <th className="ht-right">Lucro Bruto</th>
            <th className="ht-right">%</th>
            <th className="ht-right">Saldo Inicial</th>
            <th className="ht-right">Saldo Final</th>
            <th className="ht-right">{"Variação (Rua)"}</th>
            <th className="ht-right">Lucro Líquido</th>
            <th className="ht-right">%</th>
          </tr>
        </thead>
        <tbody className="body-table">
          {!somenteTotal && data.map((t: FechamentoResultadoSorteioModel) => (
            <React.Fragment key={t.idEmpresa}>
              <tr>
                <td className="ht-left">{t.nomeEmpresa === null ? <></> : t.nomeEmpresa}</td>
                <td className="ht-right">{t.valorReceitaOperacional === null ? <></> : currencyBRL(t.valorReceitaOperacional)}</td>
                <td className="ht-right">{t.valorOutrasReceitas === null ? <></> : currencyBRL(t.valorOutrasReceitas)}</td>
                <td className="ht-right">{t.valorReceitaTotal === null ? <></> : currencyBRL(t.valorReceitaTotal)}</td>
                <td className="ht-right">{t.valorDespesas === null ? <></> : currencyBRL(t.valorDespesas)}</td>
                <td className="ht-right">{percentualTexto(t.valorDespesas, t.valorReceitaOperacional, true)}</td>
                <td className="ht-right">{t.valorPremiacao === null ? <></> : currencyBRL(t.valorPremiacao)}</td>
                <td className="ht-right">{percentualTexto(t.valorPremiacao, t.valorReceitaOperacional, true)}</td>
                <td className="ht-right">{t.valorLucroBruto === null ? <></> : currencyBRL(t.valorLucroBruto)}</td>
                <td className="ht-right">{percentualTexto(t.valorLucroBruto, t.valorReceitaOperacional)}</td>
                <td className="ht-right">{t.valorSaldoInicio === null ? <></> : currencyBRL(t.valorSaldoInicio)}</td>
                <td className="ht-right">{t.valorSaldoFim === null ? <></> : currencyBRL(t.valorSaldoFim)}</td>
                <td className="ht-right">{t.valorVariacaoRua === null ? <></> : currencyBRL(t.valorVariacaoRua)}</td>
                <td className="ht-right">{t.valorLucroLiquido === null ? <></> : currencyBRL(t.valorLucroLiquido)}</td>
                <td className="ht-right">{percentualTexto(t.valorLucroLiquido, t.valorReceitaOperacional)}</td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
        <tfoot className="footer-table">
          <tr>
            <td className="ht-left">Total</td>
            <td className="ht-right">{currencyBRL(totalReceitaOperacional)}</td>
            <td className="ht-right">{currencyBRL(total((i) => i.valorOutrasReceitas))}</td>
            <td className="ht-right">{currencyBRL(total((i) => i.valorReceitaTotal))}</td>
            <td className="ht-right">{currencyBRL(totalDespesas)}</td>
            <td className="ht-right">{percentualTexto(totalDespesas, totalReceitaOperacional, true)}</td>
            <td className="ht-right">{currencyBRL(totalPremiacao)}</td>
            <td className="ht-right">{percentualTexto(totalPremiacao, totalReceitaOperacional, true)}</td>
            <td className="ht-right">{currencyBRL(totalLucroBruto)}</td>
            <td className="ht-right">{percentualTexto(totalLucroBruto, totalReceitaOperacional)}</td>
            <td className="ht-right">{currencyBRL(total((i) => i.valorSaldoInicio))}</td>
            <td className="ht-right">{currencyBRL(total((i) => i.valorSaldoFim))}</td>
            <td className="ht-right">{currencyBRL(total((i) => i.valorVariacaoRua))}</td>
            <td className="ht-right">{currencyBRL(totalLucroLiquido)}</td>
            <td className="ht-right">{percentualTexto(totalLucroLiquido, totalReceitaOperacional)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const RelatorioHtml = React.forwardRef<HTMLDivElement, RelatorioHtmlProps>(({ busca, list, listSonho, empresaList }, ref) => {
  return (
    <>
      <div ref={ref} className="relatorios-html-format">
        <div className="rel-cabecalho">
          <div style={{ display: "flex", flexDirection: "row", justifyContent: "start", alignItems: "center", marginTop: "24px", marginBottom: "24px", marginLeft: "14px" }}>
            <Image preview={false} height={50} src={icon} />
            <div className={"logoText"}>
              <div className="labelLogoGeral">
                <span>GRUPO</span>
              </div>
              <div className="labelLogoGeral">
                <span>PASCHOAL</span>
              </div>
            </div>
            <div className="rel-titulo" style={{ display: "flex", flexDirection: "column", marginLeft: "24px" }}>
              <h2>
                <strong>Composição de Resultado da Empresa</strong>
              </h2>
              {busca.listaEmpresa !== null && busca.listaEmpresa.split("|").length > 1 ? (
                <>
                  <p>{"Empresa: " + empresaList.filter((a: EmpresaModel) => busca.listaEmpresa?.split("|").includes(String(a.id))).map((a: EmpresaModel) => a.apelido)}</p>
                  <p style={{ marginTop: "-10px" }}>{"Data Inicial: " + dayjs(busca.dataInicio).format("DD/MM/YYYY")}</p>
                  <p style={{ marginTop: "-10px" }}>{"Data Final: " + dayjs(busca.dataFim).format("DD/MM/YYYY")}</p>
                </>
              ) : (
                <>
                  <p>{"Data Inicial: " + dayjs(busca.dataInicio).format("DD/MM/YYYY")}</p>
                  <p style={{ marginTop: "-10px" }}>{"Data Final: " + dayjs(busca.dataFim).format("DD/MM/YYYY")}</p>
                </>
              )}
            </div>
          </div>
        </div>
        {list.length > 0 && <RelatorioHtmlTable data={list} />}
        {listSonho.length > 0 && <RelatorioHtmlTable data={listSonho} titulo="Sonho da Sorte" />}
        {list.length > 0 && listSonho.length > 0 && <RelatorioHtmlTable data={[...list, ...listSonho]} titulo="Total Geral" somenteTotal />}
      </div>
    </>
  );
});

const columns: ColumnsType<FechamentoResultadoSorteioModel> = [
  {
    title: "EMPRESA",
    dataIndex: "nomeEmpresa",
  },
  {
    title: "RECEITAS OPERACIONAIS",
    dataIndex: "valorReceitaOperacional",
    align: "right",
    render: (_, record) => (
      <p style={{ color: record.valorReceitaOperacional !== null && record.valorReceitaOperacional < 0 ? "red" : "blue", marginBottom: "0px" }}>
        {record.valorReceitaOperacional !== null && currencyBRL(record.valorReceitaOperacional)}
      </p>
    ),
  },
  {
    title: "OUTRAS RECEITAS",
    dataIndex: "valorOutrasReceitas",
    align: "right",
    render: (_, record) => (
      <p style={{ color: record.valorOutrasReceitas !== null && record.valorOutrasReceitas < 0 ? "red" : "blue", marginBottom: "0px" }}>
        {record.valorOutrasReceitas !== null && currencyBRL(record.valorOutrasReceitas)}
      </p>
    ),
  },
  {
    title: "RECEITA TOTAL",
    dataIndex: "valorReceitaTotal",
    align: "right" as "right",
    render: (_, record) => (
      <p style={{ color: record.valorReceitaTotal !== null && record.valorReceitaTotal < 0 ? "red" : "blue", marginBottom: "0px" }}>
        {record.valorReceitaTotal !== null && currencyBRL(record.valorReceitaTotal)}
      </p>
    ),
  },
  {
    title: "DESPESAS",
    dataIndex: "valorDespesas",
    align: "right",
    render: (_, record) => (
      <p style={{ color: record.valorDespesas !== null && record.valorDespesas < 0 ? "red" : "blue", marginBottom: "0px" }}>{record.valorDespesas !== null && currencyBRL(record.valorDespesas)}</p>
    ),
  },
  {
    title: "%",
    key: "percentualDespesas",
    align: "right",
    render: (_, record) => percentualCelula(record.valorDespesas, record.valorReceitaOperacional, true),
  },
  {
    title: "PREMIAÇÃO",
    dataIndex: "valorPremiacao",
    align: "right",
    render: (_, record) => (
      <p style={{ color: record.valorPremiacao !== null && record.valorPremiacao < 0 ? "red" : "blue", marginBottom: "0px" }}>{record.valorPremiacao !== null && currencyBRL(record.valorPremiacao)}</p>
    ),
  },
  {
    title: "%",
    key: "percentualPremiacao",
    align: "right",
    render: (_, record) => percentualCelula(record.valorPremiacao, record.valorReceitaOperacional, true),
  },
  {
    title: "LUCRO BRUTO",
    dataIndex: "valorLucroBruto",
    align: "right",
    render: (_, record) => (
      <p style={{ color: record.valorLucroBruto !== null && record.valorLucroBruto < 0 ? "red" : "blue", marginBottom: "0px" }}>
        {record.valorLucroBruto !== null && currencyBRL(record.valorLucroBruto)}
      </p>
    ),
  },
  {
    title: "%",
    key: "percentualLucroBruto",
    align: "right",
    render: (_, record) => percentualCelula(record.valorLucroBruto, record.valorReceitaOperacional),
  },
  {
    title: "SALDO INICIAL",
    dataIndex: "valorSaldoInicio",
    align: "right",
    render: (_, record) => (
      <p style={{ color: record.valorSaldoInicio !== null && record.valorSaldoInicio < 0 ? "red" : "blue", marginBottom: "0px" }}>
        {record.valorSaldoInicio !== null && currencyBRL(record.valorSaldoInicio)}
      </p>
    ),
  },
  {
    title: "SALDO FINAL",
    dataIndex: "valorSaldoFim",
    align: "right",
    render: (_, record) => (
      <p style={{ color: record.valorSaldoFim !== null && record.valorSaldoFim < 0 ? "red" : "blue", marginBottom: "0px" }}>{record.valorSaldoFim !== null && currencyBRL(record.valorSaldoFim)}</p>
    ),
  },
  {
    title: "VARIAÇÃO (RUA)",
    dataIndex: "valorVariacaoRua",
    align: "right",
    render: (_, record) => (
      <p style={{ color: record.valorVariacaoRua !== null && record.valorVariacaoRua < 0 ? "red" : "blue", marginBottom: "0px" }}>
        {record.valorVariacaoRua !== null && currencyBRL(record.valorVariacaoRua)}
      </p>
    ),
  },
  {
    title: "LUCRO LÍQUIDO",
    dataIndex: "valorLucroLiquido",
    align: "right",
    render: (_, record) => (
      <p style={{ color: record.valorLucroLiquido !== null && record.valorLucroLiquido < 0 ? "red" : "blue", marginBottom: "0px" }}>
        {record.valorLucroLiquido !== null && currencyBRL(record.valorLucroLiquido)}
      </p>
    ),
  },
  {
    title: "%",
    key: "percentualLucroLiquido",
    align: "right",
    render: (_, record) => percentualCelula(record.valorLucroLiquido, record.valorReceitaOperacional),
  },
];

const Composicao = () => {
  //estados globais
  const composicaoState = useAppSelector((state) => state.composicao);
  const empresaState = useAppSelector((state) => state.empresa);
  const resultadoSorteioState = useAppSelector((state) => state.resultadoSorteio);
  const persistedState = useAppSelector((state) => state.persisted);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm();
  const relatorioComposicao = useRef<HTMLDivElement>(null);
  const [agrupado, setAgrupado] = useState(false);
  const [modalSalvasAberto, setModalSalvasAberto] = useState(false);

  const displayList = useMemo<FechamentoResultadoSorteioModel[]>(() => {
    if (!agrupado || composicaoState.list.length === 0) return composicaoState.list;

    const groups = new Map<number, FechamentoResultadoSorteioModel>();
    composicaoState.list.forEach((item) => {
      const key = item.idEmpresaResultado ?? item.idEmpresa;
      // empresa-resultado (ex: PROMOBEM) tem key === idEmpresa; empresas filhas (ex: 4WAYS) têm key !== idEmpresa
      const ehPrincipal = key === item.idEmpresa;
      const prev = groups.get(key);
      if (prev) {
        groups.set(key, {
          ...prev,
          // o nome exibido no grupo é sempre o da empresa-resultado, independente da ordem em que os itens chegam
          nomeEmpresa: ehPrincipal ? item.nomeEmpresa : prev.nomeEmpresa,
          valorReceitaOperacional: (prev.valorReceitaOperacional ?? 0) + (item.valorReceitaOperacional ?? 0),
          valorOutrasReceitas: (prev.valorOutrasReceitas ?? 0) + (item.valorOutrasReceitas ?? 0),
          valorReceitaTotal: (prev.valorReceitaTotal ?? 0) + (item.valorReceitaTotal ?? 0),
          valorDespesas: (prev.valorDespesas ?? 0) + (item.valorDespesas ?? 0),
          valorPremiacao: (prev.valorPremiacao ?? 0) + (item.valorPremiacao ?? 0),
          valorSaldoInicio: (prev.valorSaldoInicio ?? 0) + (item.valorSaldoInicio ?? 0),
          valorSaldoFim: (prev.valorSaldoFim ?? 0) + (item.valorSaldoFim ?? 0),
          valorLucroBruto: (prev.valorLucroBruto ?? 0) + (item.valorLucroBruto ?? 0),
          valorVariacaoRua: (prev.valorVariacaoRua ?? 0) + (item.valorVariacaoRua ?? 0),
          valorLucroLiquido: (prev.valorLucroLiquido ?? 0) + (item.valorLucroLiquido ?? 0),
          valorSuporteRepasse: (prev.valorSuporteRepasse ?? 0) + (item.valorSuporteRepasse ?? 0),
        });
      } else {
        // primeira ocorrência do grupo; a chave (idEmpresa) passa a ser a da empresa-resultado
        groups.set(key, { ...item, idEmpresa: key });
      }
    });
    return Array.from(groups.values());
  }, [composicaoState.list, agrupado]);

  const displayListPrincipal = useMemo(() => displayList.filter((item) => !SONHO_IDS.includes(item.idEmpresa)), [displayList]);
  const displayListSonho = useMemo(() => displayList.filter((item) => SONHO_IDS.includes(item.idEmpresa)), [displayList]);

  const renderSummaryRow = (items: FechamentoResultadoSorteioModel[], label?: string) => {
    const total = (seletor: (item: FechamentoResultadoSorteioModel) => number | null) => items.reduce((acc, item) => acc + (seletor(item) || 0), 0);
    const totalReceitaOperacional = total((i) => i.valorReceitaOperacional);
    const totalDespesas = total((i) => i.valorDespesas);
    const totalPremiacao = total((i) => i.valorPremiacao);
    const totalLucroBruto = total((i) => i.valorLucroBruto);
    const totalLucroLiquido = total((i) => i.valorLucroLiquido);

    const celulaValor = (valor: number) => ({ texto: currencyBRL(valor), positivo: valor > 0 });
    const celulaPercentual = (valor: number, inverterSinal = false) => {
      const p = percentualComposicao(valor, totalReceitaOperacional, inverterSinal);
      return { texto: p === null ? "" : formatPercent(p), positivo: (p ?? 0) > 0 };
    };

    const celulas = [
      celulaValor(totalReceitaOperacional),
      celulaValor(total((i) => i.valorOutrasReceitas)),
      celulaValor(total((i) => i.valorReceitaTotal)),
      celulaValor(totalDespesas),
      celulaPercentual(totalDespesas, true),
      celulaValor(totalPremiacao),
      celulaPercentual(totalPremiacao, true),
      celulaValor(totalLucroBruto),
      celulaPercentual(totalLucroBruto),
      celulaValor(total((i) => i.valorSaldoInicio)),
      celulaValor(total((i) => i.valorSaldoFim)),
      celulaValor(total((i) => i.valorVariacaoRua)),
      celulaValor(totalLucroLiquido),
      celulaPercentual(totalLucroLiquido),
    ];
    return (
      <Table.Summary.Row className="rowFooterCls">
        <Table.Summary.Cell index={0}>
          {label && <span style={{ fontWeight: "bold", fontSize: "13px" }}>{label}</span>}
        </Table.Summary.Cell>
        {celulas.map((celula, i) => (
          <Table.Summary.Cell key={i} index={i + 1} align="right">
            <span style={{ color: celula.positivo ? "blue" : "red", marginBottom: "0px", fontSize: "13px" }}>{celula.texto}</span>
          </Table.Summary.Cell>
        ))}
      </Table.Summary.Row>
    );
  };

  const tableSummary = (pageData: any) => pageData.length > 0 && <>{renderSummaryRow(pageData)}</>;

  useEffect(() => {
    dispatch(setTitle("COMPOSIÇÃO DE RESULTADO DA EMPRESA"));
    if (empresaState.list.length === 0) dispatch(buscarEmpresa(empresaState.busca));
    form.setFieldsValue({ dataInicio: dayjs(composicaoState.busca.dataInicio), dataFim: dayjs(composicaoState.busca.dataFim), tipoDataFiltro: composicaoState.busca.tipoFiltroData });
    if (resultadoSorteioState.comesFromResultado) {
      const busca: ListarComposicaoModel = {
        ...composicaoState.busca,
        dataInicio: resultadoSorteioState.dataSorteio,
        listaEmpresa: resultadoSorteioState.idEmpresa.toString(),
      };
      form.setFieldsValue({ dataInicio: dayjs(resultadoSorteioState.dataSorteio), dataFim: dayjs(composicaoState.busca.dataFim) });
      dispatch(listarComposicao(busca));
    }
    if (persistedState.user.empresa !== null) {
      const idEmpresa = persistedState.user.empresa.idEmpresa;
      form.setFieldsValue({ idEmpresa: [idEmpresa] });
      dispatch(setBuscaComposicao({ ...composicaoState.busca, listaEmpresa: String(idEmpresa) }));
    }
  }, []);

  const onChangeEmpresa = (values: any) => {
    const busca: ListarComposicaoModel = {
      ...composicaoState.busca,
      listaEmpresa: values.map(String).join("|"),
    };
    dispatch(setBuscaComposicao(busca));
  };

  const onChangeDateInicio = (value: DatePickerProps["value"], dateString: string | string[]) => {
    const busca: ListarComposicaoModel = { ...composicaoState.busca, dataInicio: dayjs(value).toDate() };
    dispatch(setBuscaComposicao(busca));
  };

  const onChangeDateFim = (value: DatePickerProps["value"], dateString: string | string[]) => {
    const busca: ListarComposicaoModel = { ...composicaoState.busca, dataFim: dayjs(value).toDate() };
    dispatch(setBuscaComposicao(busca));
  };

  const onChangeTipoData = (value: string) => {
    const busca: ListarComposicaoModel = { ...composicaoState.busca, tipoFiltroData: value };
    dispatch(setBuscaComposicao(busca));
  };

  const atualizar = () => {
    dispatch(listarComposicao(composicaoState.busca));
  };

  //verifica se já existe composição salva para os filtros atuais (banner + aviso de sobrescrita)
  useEffect(() => {
    const busca = composicaoState.busca;
    if (busca.listaEmpresa && busca.dataInicio && busca.dataFim && busca.tipoFiltroData) {
      dispatch(verificarComposicaoSalva(busca));
    }
  }, [composicaoState.busca]);

  const montarSalvarModel = (): SalvarComposicaoModel => ({
    tipoFiltroData: composicaoState.busca.tipoFiltroData,
    dataInicio: dayjs(composicaoState.busca.dataInicio).format("YYYY-MM-DD"),
    dataFim: dayjs(composicaoState.busca.dataFim).format("YYYY-MM-DD"),
    listaEmpresa: (composicaoState.busca.listaEmpresa ?? "").split("|").filter(Boolean).map(Number),
  });

  const onSalvar = () => {
    const payload = { value: montarSalvarModel(), busca: composicaoState.busca };
    if (composicaoState.composicaoSalvaInfo) {
      Modal.confirm({
        title: "Sobrescrever composição salva?",
        content: `Já existe uma composição salva para estes filtros (salva em ${formatDataHoraLocal(composicaoState.composicaoSalvaInfo.dataHoraAlteracao)}${
          composicaoState.composicaoSalvaInfo.nomeUsuario ? " por " + composicaoState.composicaoSalvaInfo.nomeUsuario : ""
        }). Salvar novamente irá sobrescrever os valores.`,
        okText: "Sobrescrever",
        cancelText: "Cancelar",
        onOk: () => {
          dispatch(salvarComposicao(payload));
        },
      });
    } else {
      dispatch(salvarComposicao(payload));
    }
  };

  const carregarComposicaoSalva = (item: ComposicaoSalvaModel) => {
    //alinha os filtros da tela com os da composição salva
    const busca: ListarComposicaoModel = {
      dataInicio: dayjs(item.dataInicio).toDate(),
      dataFim: dayjs(item.dataFim).toDate(),
      listaEmpresa: item.listaEmpresasAssinatura,
      tipoFiltroData: item.tipoDataFiltro?.trim(),
    };
    dispatch(setBuscaComposicao(busca));
    form.setFieldsValue({
      dataInicio: dayjs(item.dataInicio),
      dataFim: dayjs(item.dataFim),
      tipoDataFiltro: item.tipoDataFiltro?.trim(),
      idEmpresa: item.listaEmpresasAssinatura.split("|").map(Number),
    });
    dispatch(obterComposicaoSalva(item.id));
    dispatch(setExibindoComposicaoSalva({ id: item.id, dataHoraAlteracao: item.dataHoraAlteracao, nomeUsuario: item.nomeUsuario }));
    setModalSalvasAberto(false);
  };

  const abrirModalSalvas = () => {
    setModalSalvasAberto(true);
  };

  const onPrint = useReactToPrint({
    content: () => relatorioComposicao.current,
  });

  const handleExcel = () => {
    const data: any = [];

    let denominacaoEmpresa = "";
    if (composicaoState.busca.listaEmpresa) {
      const partes = composicaoState.busca.listaEmpresa.split("|");
      partes.forEach((a: any, index: number) => {
        const empresa = empresaState.list.find((empresa: EmpresaModel) => empresa.id === parseInt(a));
        if (empresa) {
          denominacaoEmpresa += empresa.apelido;
          if (index < partes.length - 1) {
            denominacaoEmpresa += ", ";
          }
        }
      });
    }

    data.push({
      "Filtro Empresa": denominacaoEmpresa,
    });

    if (composicaoState.busca.tipoFiltroData === "SORT") {
      data.push({
        "Filtro Empresa": "Data do Sorteio",
      });
      data.push({
        "Filtro Empresa": dayjs(composicaoState.busca.dataInicio).format("DD/MM/YYYY"),
      });
    } else {
      data.push({
        "Filtro Empresa": "Data Inicial",
      });
      data.push({
        "Filtro Empresa": dayjs(composicaoState.busca.dataInicio).format("DD/MM/YYYY"),
      });

      data.push({
        "Filtro Empresa": "Data Final",
      });
      data.push({
        "Filtro Empresa": dayjs(composicaoState.busca.dataFim).format("DD/MM/YYYY"),
      });
    }

    const addExcelSection = (items: FechamentoResultadoSorteioModel[], titulo?: string) => {
      if (items.length === 0) return;
      if (titulo) {
        data.push({ "Filtro Empresa": "" });
        data.push({ "Filtro Empresa": titulo });
      }
      data.push({
        "Filtro Empresa": "Empresa",
        "": "Receitas Operacionais",
        " ": "Outras Receitas",
        "  ": "Receita Total",
        "   ": "Despesas",
        "          ": "%",
        "    ": "Premiação",
        "           ": "%",
        "     ": "Lucro Bruto",
        "            ": "%",
        "      ": "Saldo Inicial",
        "       ": "Saldo Final",
        "        ": "Variação (Rua)",
        "         ": "Lucro Líquido",
        "             ": "%",
      });
      items.forEach((item: FechamentoResultadoSorteioModel) => {
        data.push({
          "Filtro Empresa": item.nomeEmpresa,
          "": item.valorReceitaOperacional !== null ? currencyBRL(item.valorReceitaOperacional) : item.valorReceitaOperacional,
          " ": item.valorOutrasReceitas !== null ? currencyBRL(item.valorOutrasReceitas) : item.valorOutrasReceitas,
          "  ": item.valorReceitaTotal !== null ? currencyBRL(item.valorReceitaTotal) : item.valorReceitaTotal,
          "   ": item.valorDespesas !== null ? currencyBRL(item.valorDespesas) : item.valorDespesas,
          "          ": percentualTexto(item.valorDespesas, item.valorReceitaOperacional, true),
          "    ": item.valorPremiacao !== null ? currencyBRL(item.valorPremiacao) : item.valorPremiacao,
          "           ": percentualTexto(item.valorPremiacao, item.valorReceitaOperacional, true),
          "     ": item.valorLucroBruto !== null ? currencyBRL(item.valorLucroBruto) : item.valorLucroBruto,
          "            ": percentualTexto(item.valorLucroBruto, item.valorReceitaOperacional),
          "      ": item.valorSaldoInicio !== null ? currencyBRL(item.valorSaldoInicio) : item.valorSaldoInicio,
          "       ": item.valorSaldoFim !== null ? currencyBRL(item.valorSaldoFim) : item.valorSaldoFim,
          "        ": item.valorVariacaoRua !== null ? currencyBRL(item.valorVariacaoRua) : item.valorVariacaoRua,
          "         ": item.valorLucroLiquido !== null ? currencyBRL(item.valorLucroLiquido) : item.valorLucroLiquido,
          "             ": percentualTexto(item.valorLucroLiquido, item.valorReceitaOperacional),
        });
      });
      const totalSecao = (seletor: (item: FechamentoResultadoSorteioModel) => number | null) => items.reduce((acc, item) => acc + (seletor(item) || 0), 0);
      const totalReceitaOperacionalSecao = totalSecao((i) => i.valorReceitaOperacional);
      const totalDespesasSecao = totalSecao((i) => i.valorDespesas);
      const totalPremiacaoSecao = totalSecao((i) => i.valorPremiacao);
      const totalLucroBrutoSecao = totalSecao((i) => i.valorLucroBruto);
      const totalLucroLiquidoSecao = totalSecao((i) => i.valorLucroLiquido);
      data.push({
        "Filtro Empresa": "Total",
        "": currencyBRL(totalReceitaOperacionalSecao),
        " ": currencyBRL(totalSecao((i) => i.valorOutrasReceitas)),
        "  ": currencyBRL(totalSecao((i) => i.valorReceitaTotal)),
        "   ": currencyBRL(totalDespesasSecao),
        "          ": percentualTexto(totalDespesasSecao, totalReceitaOperacionalSecao, true),
        "    ": currencyBRL(totalPremiacaoSecao),
        "           ": percentualTexto(totalPremiacaoSecao, totalReceitaOperacionalSecao, true),
        "     ": currencyBRL(totalLucroBrutoSecao),
        "            ": percentualTexto(totalLucroBrutoSecao, totalReceitaOperacionalSecao),
        "      ": currencyBRL(totalSecao((i) => i.valorSaldoInicio)),
        "       ": currencyBRL(totalSecao((i) => i.valorSaldoFim)),
        "        ": currencyBRL(totalSecao((i) => i.valorVariacaoRua)),
        "         ": currencyBRL(totalLucroLiquidoSecao),
        "             ": percentualTexto(totalLucroLiquidoSecao, totalReceitaOperacionalSecao),
      });
    };

    addExcelSection(displayListPrincipal);
    addExcelSection(displayListSonho, "Sonho da Sorte");

    if (displayListPrincipal.length > 0 && displayListSonho.length > 0) {
      const totalGeral = (seletor: (item: FechamentoResultadoSorteioModel) => number | null) => displayList.reduce((acc, item) => acc + (seletor(item) || 0), 0);
      const totalGeralReceitaOperacional = totalGeral((i) => i.valorReceitaOperacional);
      const totalGeralDespesas = totalGeral((i) => i.valorDespesas);
      const totalGeralPremiacao = totalGeral((i) => i.valorPremiacao);
      const totalGeralLucroBruto = totalGeral((i) => i.valorLucroBruto);
      const totalGeralLucroLiquido = totalGeral((i) => i.valorLucroLiquido);
      data.push({ "Filtro Empresa": "" });
      data.push({ "Filtro Empresa": "Total Geral" });
      data.push({
        "Filtro Empresa": "Total Geral",
        "": currencyBRL(totalGeralReceitaOperacional),
        " ": currencyBRL(totalGeral((i) => i.valorOutrasReceitas)),
        "  ": currencyBRL(totalGeral((i) => i.valorReceitaTotal)),
        "   ": currencyBRL(totalGeralDespesas),
        "          ": percentualTexto(totalGeralDespesas, totalGeralReceitaOperacional, true),
        "    ": currencyBRL(totalGeralPremiacao),
        "           ": percentualTexto(totalGeralPremiacao, totalGeralReceitaOperacional, true),
        "     ": currencyBRL(totalGeralLucroBruto),
        "            ": percentualTexto(totalGeralLucroBruto, totalGeralReceitaOperacional),
        "      ": currencyBRL(totalGeral((i) => i.valorSaldoInicio)),
        "       ": currencyBRL(totalGeral((i) => i.valorSaldoFim)),
        "        ": currencyBRL(totalGeral((i) => i.valorVariacaoRua)),
        "         ": currencyBRL(totalGeralLucroLiquido),
        "             ": percentualTexto(totalGeralLucroLiquido, totalGeralReceitaOperacional),
      });
    }

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "ComposiçãoResultadoEmpresa.xlsx");
  };

  return (
    <Flex vertical>
      <Form layout="vertical" form={form}>
        <Row gutter={12} align="bottom">
          <Col span={24}>
            <Flex gap={12}>
              {persistedState.user.empresa === null && (
                <Form.Item label="Selecione as empresas" name="idEmpresa">
                  <Select
                    showSearch
                    mode="multiple"
                    optionFilterProp="label"
                    placeholder="Selecione a empresa"
                    maxTagCount="responsive"
                    style={{ width: 550 }}
                    maxTagPlaceholder={(omittedValues) => (
                      <Tooltip
                        title={
                          <ul style={{ paddingLeft: 16, margin: 0 }}>
                            {omittedValues.map((field, index) => (
                              <li key={index}>{field.label}</li>
                            ))}
                          </ul>
                        }
                      >
                        <span>{`+ ${omittedValues.length} ...`}</span>
                      </Tooltip>
                    )}
                    onChange={onChangeEmpresa}
                    loading={empresaState.loading}
                    options={empresaState.list.map((a: any) => ({
                      value: a.id,
                      label: a.apelido,
                    }))}
                  />
                </Form.Item>
              )}
              <Form.Item
                label="Tipo de data"
                name="tipoDataFiltro"
                rules={[
                  {
                    required: true,
                    message: "Selecione um tipo de data para o filtro",
                  },
                ]}
              >
                <Select onChange={onChangeTipoData} style={{ width: 150 }} options={tipoDataOptions} />
              </Form.Item>
              <Form.Item label="Data início" name="dataInicio">
                <DatePicker placeholder="" onChange={onChangeDateInicio} format="DD/MM/YYYY" style={{ width: 120 }} />
              </Form.Item>
              <Form.Item label="Data fim" name="dataFim">
                <DatePicker placeholder="" onChange={onChangeDateFim} format="DD/MM/YYYY" style={{ width: 120 }} />
              </Form.Item>
              <Form.Item label=" ">
                <Tooltip title="Atualizar Composição">
                  <Button icon={<RedoOutlined />} type="primary" onClick={atualizar} disabled={composicaoState.loading}></Button>
                </Tooltip>
              </Form.Item>
              <Form.Item label=" ">
                <Tooltip title="Salvar Composição">
                  <Button
                    icon={<SaveOutlined />}
                    onClick={onSalvar}
                    disabled={composicaoState.loading || composicaoState.list.length === 0 || !composicaoState.busca.listaEmpresa || composicaoState.exibindoComposicaoSalva !== null}
                  ></Button>
                </Tooltip>
              </Form.Item>
              <Form.Item label=" ">
                <Tooltip title="Carregar Composições Salvas">
                  <Button icon={<FolderOpenOutlined />} onClick={abrirModalSalvas} disabled={composicaoState.loading}></Button>
                </Tooltip>
              </Form.Item>
              <Form.Item name="botaoImprimir" label=" ">
                <Button icon={<PrinterOutlined />} onClick={onPrint} disabled={composicaoState.loading || composicaoState.list.length === 0}></Button>
              </Form.Item>
              <Form.Item name="botaoExcel" label=" ">
                <Button icon={<FileExcelOutlined />} onClick={handleExcel} disabled={composicaoState.loading || composicaoState.list.length === 0}></Button>
              </Form.Item>
              <Form.Item label="Agrupado">
                <Switch checked={agrupado} onChange={setAgrupado} disabled={composicaoState.list.length === 0} />
              </Form.Item>
            </Flex>
          </Col>
        </Row>
      </Form>
      {composicaoState.exibindoComposicaoSalva !== null ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Exibindo composição salva em ${formatDataHoraLocal(composicaoState.exibindoComposicaoSalva.dataHoraAlteracao)}${
            composicaoState.exibindoComposicaoSalva.nomeUsuario ? " por " + composicaoState.exibindoComposicaoSalva.nomeUsuario : ""
          }.`}
          action={
            <Button className="buttonPrimary" size="small" icon={<RedoOutlined />} onClick={atualizar}>
              Recalcular
            </Button>
          }
        />
      ) : (
        composicaoState.composicaoSalvaInfo !== null && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`Existe uma composição salva para estes filtros (salva em ${formatDataHoraLocal(composicaoState.composicaoSalvaInfo.dataHoraAlteracao)}${
              composicaoState.composicaoSalvaInfo.nomeUsuario ? " por " + composicaoState.composicaoSalvaInfo.nomeUsuario : ""
            }).`}
            action={
              <Button
                className="buttonPrimary"
                size="small"
                onClick={() => {
                  dispatch(obterComposicaoSalva(composicaoState.composicaoSalvaInfo!.id));
                  dispatch(setExibindoComposicaoSalva(composicaoState.composicaoSalvaInfo));
                }}
              >
                Carregar versão salva
              </Button>
            }
          />
        )
      )}
      {(displayListPrincipal.length > 0 || composicaoState.loading) && (
        <TableComponent
          columns={columns}
          data={displayListPrincipal}
          loading={composicaoState.loading}
          rowKey="idEmpresa"
          pagination={false}
          tableProps={{
            className: "tableComposicao",
            summary: (pageData: any) => tableSummary(pageData),
          }}
        />
      )}
      {displayListSonho.length > 0 && (
        <>
          <h3 style={{ marginTop: "16px", marginBottom: "8px" }}>Sonho da Sorte</h3>
          <TableComponent
            columns={columns}
            data={displayListSonho}
            loading={composicaoState.loading}
            rowKey="idEmpresa"
            pagination={false}
            tableProps={{
              className: "tableComposicao",
              summary: (pageData: any) =>
                pageData.length > 0 && (
                  <>
                    {renderSummaryRow(pageData)}
                    {displayListPrincipal.length > 0 && (
                      <>
                        <Table.Summary.Row className="rowSpacer">
                          <Table.Summary.Cell index={0} colSpan={columns.length} />
                        </Table.Summary.Row>
                        {renderSummaryRow(displayList, "Total Geral")}
                      </>
                    )}
                  </>
                ),
            }}
          />
        </>
      )}
      <ModalComposicoesSalvas open={modalSalvasAberto} onClose={() => setModalSalvasAberto(false)} onSelecionar={carregarComposicaoSalva} />
      <div style={{ display: "none" }}>
        <RelatorioHtml ref={relatorioComposicao} busca={composicaoState.busca} list={displayListPrincipal} listSonho={displayListSonho} empresaList={empresaState.list} />
      </div>
    </Flex>
  );
};

export default Composicao;
